import { useEffect, useRef, useState } from 'react';
import { findChipAtPoint, type MentionTokenChip } from './hooks/useMentionTokenChips';
import { MentionChipHoverPreview, type MentionChipHoverAnchor } from './MentionChipHoverPreview';
import styles from './MentionTokenChipOverlay.module.css';

interface MentionTokenChipOverlayProps {
  chips: MentionTokenChip[];
}

const FALLBACK_BACKGROUND = '#141414';
// チップ幅がこれ未満ならサムネイルのみ表示する (サムネイル+文字の両方は入り切らないため)。
const MIN_WIDTH_FOR_LABEL = 30;
// ホバーからプレビュー表示までの遅延 (MentionTokenInlineText と同じ値で揃える)。
const HOVER_SHOW_DELAY_MS = 150;

type HoverPreviewState = {
  anchor: MentionChipHoverAnchor;
  thumbnailUrl: string | null;
  name: string | null;
  kind: 'material' | 'actor';
};

function parseCssColor(value: string): { r: number; g: number; b: number; a: number } | null {
  const match = value.match(/^rgba?\(([^)]+)\)$/);
  if (!match) return null;
  const parts = match[1].split(',').map((p) => parseFloat(p.trim()));
  const [r, g, b] = parts;
  const a = parts.length > 3 ? parts[3] : 1;
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return { r, g, b, a: Number.isNaN(a) ? 1 : a };
}

// fg (手前) を bg (奥) の上に単純アルファ合成する (src-over)。
function composeOver(
  fg: { r: number; g: number; b: number; a: number },
  bg: { r: number; g: number; b: number; a: number },
) {
  const a = fg.a + bg.a * (1 - fg.a);
  if (a <= 0) return { r: 0, g: 0, b: 0, a: 0 };
  return {
    r: (fg.r * fg.a + bg.r * bg.a * (1 - fg.a)) / a,
    g: (fg.g * fg.a + bg.g * bg.a * (1 - fg.a)) / a,
    b: (fg.b * fg.a + bg.b * bg.a * (1 - fg.a)) / a,
    a,
  };
}

/**
 * el 自身から親要素を辿って computed backgroundColor を単純アルファ合成し、実際に
 * 画面上で見えている実効不透明色を求める。textarea 自体の背景が半透明
 * (rgba(12,12,12,.6〜.8)) のため、チップの背景をこの実効色ベースにしないと
 * チップだけ周囲から浮いて見える。alpha 累積が 0.99 を超えたら打ち切り、
 * 最後まで不透明にならなければ fallback を返す。
 */
function getEffectiveOpaqueBackground(el: Element | null): string {
  let acc = { r: 0, g: 0, b: 0, a: 0 };
  let node: Element | null = el;
  while (node) {
    const bg = parseCssColor(window.getComputedStyle(node).backgroundColor);
    if (bg && bg.a > 0) {
      acc = composeOver(acc, bg);
      if (acc.a > 0.99) break;
    }
    node = node.parentElement;
  }
  if (acc.a <= 0.99) return FALLBACK_BACKGROUND;
  const clamp = (n: number) => Math.round(Math.max(0, Math.min(255, n)));
  return `rgb(${clamp(acc.r)}, ${clamp(acc.g)}, ${clamp(acc.b)})`;
}

/**
 * 素材 @メンショントークン (`@A#3f9c`) の上に「サムネイル+バッジ」チップを重ねる
 * 表示専用オーバーレイ (docs/features/material-at-mention-plan.md
 * 「トークンのチップ表示 (オーバーレイ装飾方式)」節)。本文 (トークン文字列) には
 * 一切触れない。**textarea の直後の兄弟要素として配置する前提**
 * (背景色の実効不透明化に previousElementSibling を使う)。
 */
export function MentionTokenChipOverlay({ chips }: MentionTokenChipOverlayProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [chipBackground, setChipBackground] = useState<string>(FALLBACK_BACKGROUND);

  // 背景色の算出は overlay マウント時に1回だけ (テーマ変化は考慮不要)。
  useEffect(() => {
    const textareaEl = rootRef.current?.previousElementSibling ?? null;
    setChipBackground(getEffectiveOpaqueBackground(textareaEl));
  }, []);

  // ホバー拡大プレビュー (MentionChipHoverPreview)。オーバーレイ自体は pointer-events:none
  // (テキスト編集を阻害しない設計) を維持するため、代わりに textarea 側の mousemove を
  // 監視して chips 配列とヒットテストする方式にする
  // (docs/features/material-at-mention-plan.md「トークンのチップ表示」節)。
  const [hoverPreview, setHoverPreview] = useState<HoverPreviewState | null>(null);
  // mousemove ハンドラは下の effect でマウント時に1回だけ張るため、最新の chips を
  // 参照するには ref 経由にする (クロージャの stale 化を防ぐ)。
  const chipsRef = useRef<MentionTokenChip[]>(chips);
  const hoveredKeyRef = useRef<string | null>(null);
  useEffect(() => {
    chipsRef.current = chips;
    // 再計測 (typing 等) で chips 配列が変わったら、表示中/表示待ちのプレビューは
    // 古い矩形のままになるためいったん隠す。マウスが同じ位置に留まっていても
    // 次の mousemove で再評価されて出し直される。
    hoveredKeyRef.current = null;
    setHoverPreview(null);
  }, [chips]);

  // previousElementSibling (= textarea) への mousemove/mouseleave/scroll/blur 購読。
  // このコンポーネント自体は SceneDetailColumn/PromptModePanel で編集セッションごとに
  // 都度マウント・アンマウントされる (常時マウントされたまま ref の付け外しだけが
  // 起きる useMentionTokenChips とは事情が異なる) ため、マウント中は textarea 要素が
  // 変わらない。よってこの effect は「毎マウントで1回だけ張って後始末する」素朴な形で
  // StrictMode の二重マウント (マウント→クリーンアップ→再マウント) にも安全に対応できる
  // (observedElRef 方式の「同じ要素なら早期return」の罠は、ref の付け外しを検知する
  // 目的の useMentionTokenChips 固有の事情であり、ここでは不要)。
  useEffect(() => {
    const ta = rootRef.current?.previousElementSibling as HTMLTextAreaElement | null;
    if (!ta) return;

    let showTimeout: number | null = null;
    const clearShowTimeout = () => {
      if (showTimeout !== null) {
        window.clearTimeout(showTimeout);
        showTimeout = null;
      }
    };
    const hide = () => {
      clearShowTimeout();
      hoveredKeyRef.current = null;
      setHoverPreview(null);
    };

    const onMouseMove = (e: MouseEvent) => {
      const rect = ta.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const chip = findChipAtPoint(chipsRef.current, x, y);
      if (!chip) {
        if (hoveredKeyRef.current !== null) hide();
        return;
      }
      // 既に同じチップを表示中/表示待ちなら何もしない (ちらつき・タイマー再始動の防止)。
      if (hoveredKeyRef.current === chip.key) return;
      clearShowTimeout();
      hoveredKeyRef.current = chip.key;
      const anchor: MentionChipHoverAnchor = {
        top: rect.top + chip.top,
        left: rect.left + chip.left,
        width: chip.width,
        height: chip.height,
      };
      showTimeout = window.setTimeout(() => {
        showTimeout = null;
        setHoverPreview({ anchor, thumbnailUrl: chip.thumbnailUrl, name: chip.name, kind: chip.kind });
      }, HOVER_SHOW_DELAY_MS);
    };

    ta.addEventListener('mousemove', onMouseMove);
    ta.addEventListener('mouseleave', hide);
    ta.addEventListener('scroll', hide);
    ta.addEventListener('blur', hide);

    return () => {
      clearShowTimeout();
      ta.removeEventListener('mousemove', onMouseMove);
      ta.removeEventListener('mouseleave', hide);
      ta.removeEventListener('scroll', hide);
      ta.removeEventListener('blur', hide);
    };
  }, []);

  return (
    <div ref={rootRef} className={styles.overlay} aria-hidden="true">
      {chips.map((chip) => {
        const showLabel = chip.width >= MIN_WIDTH_FOR_LABEL;
        return (
          <div
            key={chip.key}
            className={styles.chip}
            style={{
              top: chip.top,
              left: chip.left,
              width: chip.width,
              height: chip.height,
              backgroundColor: chipBackground,
            }}
          >
            {chip.thumbnailUrl && (
              <img
                src={chip.thumbnailUrl}
                alt=""
                draggable={false}
                className={`${styles.thumb} ${chip.kind === 'actor' ? styles.thumbActor : ''}`}
              />
            )}
            {showLabel && <span className={styles.label}>{chip.label}</span>}
          </div>
        );
      })}
      {hoverPreview && (
        <MentionChipHoverPreview
          anchor={hoverPreview.anchor}
          thumbnailUrl={hoverPreview.thumbnailUrl}
          name={hoverPreview.name}
          kind={hoverPreview.kind}
        />
      )}
    </div>
  );
}
